<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { whatsappTabs } from "@/config/whatsapp/whatsappTabs";

const router = useRouter();
const route = useRoute("whatsapp-tab");
const { t, locale } = useI18n({ useScope: "global" });

const drawer = ref(false);
const legacyRedirects: Record<string, string> = {
  inbox: "live-msgs",
  send: "test-send",
  automation: "actions",
  settings: "setup",
  account: "setup",
};

definePage({
  meta: {
    // definePage meta is hoisted out of component scope at compile time, so it
    // must be a static literal — a runtime import like PAGE_KEYS.WHATSAPP would
    // throw "PAGE_KEYS is not defined". Value mirrors PAGE_KEYS.WHATSAPP.
    permissionKey: "page.whatsapp",
    key: "tab",
  },
});

const componentMap = Object.fromEntries(
  whatsappTabs.map(item => [item.tab, defineAsyncComponent(item.loader)]),
) as Record<string, ReturnType<typeof defineAsyncComponent>>;

const tabs = computed(() =>
  whatsappTabs.map(item => ({
    ...item,
    title: t(item.titleKey),
  })),
);

const activeTab = computed({
  get: () => route.params.tab,
  set: tab => router.push({ name: "whatsapp-tab", params: { tab } }),
});

const currentTitle = computed(() => {
  const tab = route.params.tab;
  const matched = tabs.value.find(item => item.tab === tab);
  return matched ? matched.title : t("alkokh.sidebar.whatsapp");
});

const currentComponent = computed(() => {
  const currentTab = String(route.params.tab || whatsappTabs[0]?.tab || "");
  return componentMap[currentTab] ?? componentMap[whatsappTabs[0]?.tab || ""];
});

function normalizeTab() {
  const tab = String(route.params.tab || "");
  const target = legacyRedirects[tab];
  if (target)
    router.replace({ name: "whatsapp-tab", params: { tab: target }, query: route.query });
  else if (!componentMap[tab])
    router.replace({ name: "whatsapp-tab", params: { tab: "live-msgs" }, query: route.query });
}
watch(() => route.params.tab, normalizeTab);
onMounted(normalizeTab);
</script>

<template>
  <div class="whatsapp-page-shell">
    <VLayout class="whatsapp-page-shell__layout">
      <VAppBar elevation="0" class="whatsapp-page-shell__bar">
        <VAppBarNavIcon
          variant="text"
          color="secondary"
          @click.stop="drawer = !drawer"
        />
        <VToolbarTitle>{{ currentTitle }}</VToolbarTitle>
      </VAppBar>

      <VNavigationDrawer
        v-model="drawer"
        class="scroll-y whatsapp-page-shell__drawer"
      >
        <VTabs
          v-model="activeTab"
          class="v-tabs-pill my-2"
          direction="vertical"
          style="padding-bottom: 16px"
        >
          <VTab
            v-for="item in tabs"
            :key="item.tab"
            :value="item.tab"
            :to="{ name: 'whatsapp-tab', params: { tab: item.tab } }"
          >
            <VIcon size="20" start :icon="item.icon" />
            {{ item.title }}
          </VTab>
        </VTabs>
      </VNavigationDrawer>

      <VMain class="whatsapp-page-shell__main">
        <component
          :is="currentComponent"
          :key="`${activeTab}-${locale}`"
        />
      </VMain>
    </VLayout>
  </div>
</template>

<style scoped>
.whatsapp-page-shell,
.whatsapp-page-shell__layout {
  background: transparent;
}

/* Bound the shell to the viewport below the app navbar so height-based panels
   (the chat inbox) get a real box to fill and scroll inside — the default
   layout's content area otherwise just grows with content. */
.whatsapp-page-shell__layout {
  /* Divided by the in-app zoom: inside .page-zoom-surface a viewport unit is zoomed too (App.vue). */
  block-size: calc(100dvh / var(--alkokh-page-zoom, 1) - 5rem);
  min-block-size: 520px;
  overflow: hidden;
}

.whatsapp-page-shell__bar,
.whatsapp-page-shell__drawer {
  background: rgb(var(--v-theme-surface));
}

/* The main region is the scroll boundary: growing panels (dashboard, forms)
   scroll here inside the bounded box; the chat inbox fills it exactly and manages
   its own internal pane scrolling (it sets overflow:hidden on itself). */
.whatsapp-page-shell__main {
  display: flex;
  min-block-size: 0;
  overflow-y: auto;
}

.whatsapp-page-shell__main :deep(> *) {
  flex: 1 1 auto;
  min-block-size: 0;
}

.v-navigation-drawer {
  overflow-y: auto !important;
}

.v-navigation-drawer .v-tabs {
  overflow-y: auto;
}
</style>
